/**
 * parallel-editor-v2 — the PROMPT DOCK. A conversation-style prompt area docked to the bottom
 * of the editor (like the old document-conversation UX): type a free-form prompt about the
 * current selection — or the whole document when nothing is selected — run it, and watch the
 * result arrive as a reviewable proposal. Keeps a session-local history of exchanges.
 *
 * Pure chrome: consumes plain shapes from @docit/editor-core via callback props; the actual
 * AI flow (authorize → generate → dispatch) lives in EditorV2's `onRun`.
 */

import * as React from 'react';
import type { SelectionInfo } from '@docit/editor-core';

/** Where a dock run lands: an explicit range plus the edit op used for the proposal. */
export interface PromptDockTarget {
  from: number;
  to: number;
  op: 'replace' | 'insert';
  /**
   * Present on 'Run with AI' object targets: the live object to insert AFTER. The runner
   * re-resolves the range from this id at run time — `from`/`to` are only the click-time
   * snapshot and go stale as soon as anyone edits above the object.
   */
  objectId?: string;
}

/** One resolved dock run handed to the host flow (plain shapes only). */
export interface PromptRunRequest {
  prompt: string;
  target: PromptDockTarget;
  selectedText: string;
}

/** Outcome of a dock run, rendered on the matching history entry. */
export type PromptRunOutcome =
  | { kind: 'proposal' }
  | { kind: 'applied' }
  | { kind: 'failed'; message: string };

/** Imperative surface for "Ask AI" / object actions: focus the dock, optionally prefilled. */
export interface PromptDockHandle {
  focus(prefill?: string, target?: PromptDockTarget): void;
}

export interface PromptDockProps {
  /** Live selection snapshot from the editor (null before mount). */
  selection: SelectionInfo | null;
  /** True while an AI action is in flight anywhere — disables input with a spinner. */
  busy: boolean;
  /** Re-read the live selection at run time (the retained snapshot may be stale). */
  getSelectionInfo(): SelectionInfo | null;
  /** Whole-document markdown for whole-doc / insert-target prompts (host caps the length). */
  getMarkdown(): string;
  /** editor-core's clamped empty-range helper; null when the editor is unmounted. */
  insertAt(pos: number): { from: number; to: number } | null;
  /** Execute the run (authorize → generate → dispatch as proposal) and report the outcome. */
  onRun(req: PromptRunRequest): Promise<PromptRunOutcome>;
}

/** Retained selection snapshot — survives the selection collapsing while the user types. */
interface SelectionSnapshot {
  from: number;
  to: number;
  text: string;
}

interface HistoryEntry {
  id: number;
  prompt: string;
  status: 'thinking' | 'proposal' | 'applied' | 'failed';
  message?: string;
  ts: string;
}

const HISTORY_VISIBLE_MAX = 8;
const CHIP_TEXT_MAX = 60;
const WHOLE_DOC_CONTEXT_MAX = 8000;
/** Any position ≥ doc size clamps to the end — the whole-doc "append here" target. */
const DOC_END = Number.MAX_SAFE_INTEGER;

export const PromptDock = React.forwardRef<PromptDockHandle, PromptDockProps>(function PromptDock(
  props,
  ref,
): React.JSX.Element {
  const [text, setText] = React.useState('');
  const [snapshot, setSnapshot] = React.useState<SelectionSnapshot | null>(null);
  const [forced, setForced] = React.useState<PromptDockTarget | null>(null);
  // Chip dismissed via ✕ — the run goes whole-document even though the editor's selection
  // still holds the old range (PM never collapses it when focus moves into the dock).
  const [dismissed, setDismissed] = React.useState(false);
  const [history, setHistory] = React.useState<HistoryEntry[]>([]);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  // Last SEEN non-empty selection (never cleared on dismiss) — identity-stable re-emits of the
  // same range (scroll refresh, unrelated transactions) must not resurrect a dismissed chip.
  const lastSelRef = React.useRef<SelectionSnapshot | null>(null);
  const idRef = React.useRef(0);

  // Retain the last non-empty selection so the prompt can still target it after the user
  // clicks into the dock (which collapses the live selection). Only a GENUINELY different
  // selection re-snapshots (and un-dismisses) — not every new SelectionInfo object identity.
  const sel = props.selection;
  React.useEffect(() => {
    if (sel && !sel.empty && sel.text.trim()) {
      const prev = lastSelRef.current;
      if (prev && prev.from === sel.from && prev.to === sel.to && prev.text === sel.text) return;
      const next = { from: sel.from, to: sel.to, text: sel.text };
      lastSelRef.current = next;
      setSnapshot(next);
      setDismissed(false);
    }
  }, [sel]);

  React.useImperativeHandle(
    ref,
    () => ({
      focus(prefill?: string, target?: PromptDockTarget) {
        if (target) setForced(target);
        if (prefill !== undefined) setText(prefill);
        textareaRef.current?.focus();
      },
    }),
    [],
  );

  const clearTarget = () => {
    setSnapshot(null);
    setForced(null);
    setDismissed(true);
  };

  const run = async () => {
    const prompt = text.trim();
    if (!prompt || props.busy) return;

    // Resolve the target NOW (not at snapshot time): live selection wins, then the retained
    // snapshot (re-validated against the current doc), then the whole document. A dismissed
    // chip skips BOTH selection branches — the editor's selection still holds the old range
    // (it never collapsed when focus moved into the dock), but the user asked for whole-doc.
    let target: PromptDockTarget;
    let selectedText: string;
    const live = dismissed ? null : props.getSelectionInfo();
    if (forced) {
      target = forced;
      selectedText = props.getMarkdown().slice(0, WHOLE_DOC_CONTEXT_MAX);
    } else if (live && !live.empty) {
      target = { from: live.from, to: live.to, op: 'replace' };
      selectedText = live.text;
    } else if (snapshot) {
      // Stale beyond the current doc (it shrank since the snapshot) → clamp and insert instead
      // of replacing a range that no longer exists.
      const clamped = props.insertAt(snapshot.to);
      if (!clamped) return;
      target =
        clamped.from < snapshot.to
          ? { from: clamped.from, to: clamped.to, op: 'insert' }
          : { from: snapshot.from, to: snapshot.to, op: 'replace' };
      selectedText = snapshot.text;
    } else {
      const end = props.insertAt(DOC_END);
      if (!end) return;
      target = { from: end.from, to: end.to, op: 'insert' };
      selectedText = props.getMarkdown().slice(0, WHOLE_DOC_CONTEXT_MAX);
    }

    const id = ++idRef.current;
    setHistory((h) => [
      { id, prompt, status: 'thinking' as const, ts: new Date().toLocaleTimeString() },
      ...h,
    ]);
    setText('');
    setForced(null); // one-shot: an object-action target applies to this run only

    const outcome = await props.onRun({ prompt, target, selectedText });
    setHistory((h) =>
      h.map((e) =>
        e.id === id
          ? {
              ...e,
              status: outcome.kind,
              message: outcome.kind === 'failed' ? outcome.message : undefined,
            }
          : e,
      ),
    );
  };

  const statusLine = (e: HistoryEntry): string => {
    switch (e.status) {
      case 'thinking':
        return 'thinking…';
      case 'proposal':
        return 'proposal ready — review above';
      case 'applied':
        return 'applied directly';
      case 'failed':
        return 'failed: ' + (e.message ?? 'unknown error');
    }
  };

  // Grow with content: 2 rows minimum, ~6 maximum.
  const rows = Math.min(6, Math.max(2, text.split('\n').length));

  const chipLabel = snapshot
    ? 'Selected: "' +
      (snapshot.text.length > CHIP_TEXT_MAX ? snapshot.text.slice(0, CHIP_TEXT_MAX) + '…' : snapshot.text) +
      '"'
    : null;

  return (
    <div className="docit-promptdock" aria-label="AI prompt dock">
      <div className="docit-promptdock__context">
        {forced ? (
          <span className="docit-promptdock__chip">
            Insert after object
            <button type="button" aria-label="Clear target" onClick={clearTarget}>
              ✕
            </button>
          </span>
        ) : chipLabel ? (
          <span className="docit-promptdock__chip">
            {chipLabel}
            <button type="button" aria-label="Clear selection target" onClick={clearTarget}>
              ✕
            </button>
          </span>
        ) : (
          <span className="docit-promptdock__chip docit-promptdock__chip--doc">Whole document</span>
        )}
      </div>

      <div className="docit-promptdock__inputrow">
        <textarea
          ref={textareaRef}
          value={text}
          rows={rows}
          disabled={props.busy}
          placeholder="Ask AI about the selection — or the whole document…"
          aria-label="AI prompt"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // isComposing: Enter that confirms an IME candidate must not submit the prompt.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void run();
            }
          }}
        />
        <button
          type="button"
          className="docit-promptdock__run"
          disabled={props.busy || !text.trim()}
          onClick={() => void run()}
        >
          {props.busy ? (
            <>
              <span className="docit-promptdock__spinner" aria-hidden="true" /> Running…
            </>
          ) : (
            '✦ Run'
          )}
        </button>
      </div>

      {history.length > 0 && (
        <ol className="docit-promptdock__history" aria-label="Prompt history">
          {history.slice(0, HISTORY_VISIBLE_MAX).map((e) => (
            <li key={e.id} className="docit-promptdock__entry" data-status={e.status}>
              <span className="docit-promptdock__entry-prompt">{e.prompt}</span>
              <span className="docit-promptdock__entry-status">{statusLine(e)}</span>
              <span className="docit-promptdock__entry-ts">{e.ts}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
});
